import { AfterViewInit, Component, computed, ElementRef, inject, input, model, OnDestroy, output, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import HLS from 'hls.js';
import { DisplayMedia } from 'src/app/models/image';
import { LoggingService } from 'src/app/services/logging.service';
import { IonIcon, IonSpinner, IonText } from "@ionic/angular/standalone";
import { LocalizationModule } from 'src/app/modules/localization/localization.module';
import { ComponentMemory } from 'src/app/models/memory';
import { Subscription } from 'rxjs';
import { wait } from 'src/app/utils/async';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  standalone: true,
  imports: [IonText, IonSpinner, IonIcon, LocalizationModule]
})
export class VideoPlayerComponent implements AfterViewInit, OnDestroy, ComponentMemory {

  loggingService = inject(LoggingService);
  toastService = inject(ToastService);

  media = input.required<DisplayMedia>();
  video = viewChild<ElementRef<HTMLVideoElement>>('video');
  muted = model(false);
  paused = model(true);
  mutedChanged$ = toObservable(this.muted);
  isStreamable = computed(() => !!this.media().fileReferenceId);
  videoSrc = computed(() => {
    if (this.isStreamable()) return;
    return this.media().src;
  });
  subscription = new Subscription();
  initialized = signal(false);
  loop = input<boolean>(false);
  close = output<void>();
  controlsHidden = signal(false);
  waiting = signal(false); // waiting for video data to arrive
  hls = signal<HLS | undefined>(undefined);

  topControls = viewChild<ElementRef<HTMLDivElement>>('topControls');
  bottomControls = viewChild<ElementRef<HTMLDivElement>>('bottomControls');

  ngAfterViewInit() {
    this.init();
  }

  ngOnDestroy(): void {
    this.dispose();
  }

  init(): void {
    if (this.initialized()) return;
    if (this.subscription.closed) {
      this.subscription = new Subscription();
    }
    this.attachHlsPlayer();

    const mutedSubscription = this.mutedChanged$.subscribe((muted) => this.setMuteValue(muted));
    this.subscription.add(mutedSubscription);
    this.initialized.set(true);
    this.video()?.nativeElement.addEventListener('waiting', this.runWaiting);
    this.video()?.nativeElement.addEventListener('playing', this.runPlaying);
  }

  dispose(): void {
    this.subscription.unsubscribe();
    this.initialized.set(false);
    this.hls()?.off(HLS.Events.MEDIA_ATTACHED, this.loadSource);
    this.video()?.nativeElement.removeEventListener('waiting', this.runPlaying);
    this.video()?.nativeElement.removeEventListener('playing', this.runPlaying);
  }

  runWaiting = (event: Event) => {
    const videoElement = event.target as HTMLVideoElement;
    if (videoElement.networkState === videoElement.NETWORK_LOADING) {
      this.waiting.set(true);
    }
  };

  runPlaying = async (event: Event) => {
    this.waiting.set(false);
  };

  async play() {
    await this.video()?.nativeElement.play();
    this.paused.set(false);
    this.scheduleHideControls();
  }

  pause() {
    this.video()?.nativeElement.pause();
    this.paused.set(true);
  }

  toggleMute() {
    const video = this.video();
    if (!video) return;
    video.nativeElement.muted = !video.nativeElement.muted;
    this.muted.set(video.nativeElement.muted);
  }

  togglePlay(event?: Event) {
    event?.stopPropagation();
    const video = this.video();
    if (!video) return;
    if (video.nativeElement.paused) {
      this.play();
    } else {
      this.pause();
    }
    this.paused.set(video.nativeElement.paused);
  }

  setMuteValue(muted: boolean) {
    const video = this.video();
    if (!video) return;
    video.nativeElement.muted = muted;
  }

  attachHlsPlayer() {
    if (!HLS.isSupported()) return;
    if (!this.isStreamable()) return;
    const src = this.media().src;
    if (!src) return;
    const video = this.video()?.nativeElement;
    if (!video) {
      this.loggingService.error('Video element not found. Error code 1118');
      this.toastService.error("There was an error playing the video");
      return;
    };
    if (video.canPlayType('application/vnd.apple.mpegurl')) { // for Safari
      video.src = src;
      return;
    }
    const hls = new HLS({
      xhrSetup(xhr, url) {
        xhr.withCredentials = false;
      },
    });
    this.hls.set(hls);
    hls.attachMedia(video);
    hls.on(HLS.Events.MEDIA_ATTACHED, this.loadSource);
    video.load();
  }

  loadSource = () => {
    const src = this.media().src;
    if (!src) return;
    this.hls()?.loadSource(src);
  };

  overlayClicked(event: Event) {
    event.stopPropagation();
  }

  async scheduleHideControls() {
    await wait(3_500);
    this.hideControls();
  }

  hideControls() {
    this.controlsHidden.set(true);

    const topControlsContainer = this.topControls()?.nativeElement;
    if (topControlsContainer) {
      topControlsContainer.style.top = `calc(0px - ${getComputedStyle(topControlsContainer).height})`;
    }

    const bottomControlsContainer = this.bottomControls()?.nativeElement;
    if (bottomControlsContainer) {
      bottomControlsContainer.style.bottom = `calc(0px - ${getComputedStyle(bottomControlsContainer).height})`;
    }
  }

  async showControls(options?: ShowControlsOptions) {
    if (!this.controlsHidden()) return;
    this.controlsHidden.set(false);
    if (options?.autoHide) {
      this.scheduleHideControls();
    }

    const topControlsContainer = this.topControls()?.nativeElement;
    if (topControlsContainer) {
      topControlsContainer.style.top = `0px`;
    }

    const bottomControlsContainer = this.bottomControls()?.nativeElement;
    if (bottomControlsContainer) {
      bottomControlsContainer.style.bottom = `0px`;
    }
  }

  dismiss() {
    this.close.emit();
  }
}

interface ShowControlsOptions {
  autoHide?: boolean;
}